import type { LabeledValuePayload } from '@Application/Users/UserStatuses/Actions/userStatusesFetched';
import type {
	DocumentFieldEntryPayload,
	DocumentFieldValuePayload,
} from '@Application/Beneficiaries/Documents/Actions/documentFetched';
import buildDocumentEvidenceView, {
	type DocumentEvidenceView,
} from '@UI/Store/Documents/Views/buildDocumentEvidenceView';
import LabeledValueView from '@UI/Store/Shared/Views/LabeledValueView';

export type DocumentFieldValue =
	| string
	| number
	| boolean
	| LabeledValueView
	| readonly LabeledValueView[]
	| null;

/** Reads the runtime shape directly — array → choice multiple, object → choice simple, anything else
 * kept as-is — rather than re-deciding from `fieldType`: the wire already typed the value correctly.
 * A `date` value is a calendar string `AAAA-MM-JJ`, never converted to a `Date` (a time zone shift
 * would move the day). */
const resolveValue = (value: DocumentFieldValuePayload): DocumentFieldValue => {
	if (Array.isArray(value)) {
		return value.map((option) => new LabeledValueView(option));
	}

	if (value !== null && typeof value === 'object') {
		// TypeScript does not narrow a `readonly T[]` out of a union on the negative branch of
		// `Array.isArray` (a known limitation) — the runtime check above already excludes it.
		return new LabeledValueView(value as LabeledValuePayload);
	}

	return value;
};

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor.
 * A leaf field, `type: 'field'` discriminant.
 */
class DocumentFieldView {
	readonly type: 'field';
	readonly key: string;
	readonly uuid: string;
	readonly path: string;
	readonly label: string;
	readonly description: string | null;
	readonly fieldType: LabeledValueView;
	readonly isMultiple: boolean;
	readonly value: DocumentFieldValue;
	readonly options: readonly LabeledValueView[] | null;
	readonly verification: LabeledValueView;
	readonly isPendingReview: boolean;
	readonly isRejectedValue: boolean;
	readonly canConfirm: boolean;
	readonly reviewedById: number | null;
	readonly reviewedAt: string | null;
	readonly evidence: readonly DocumentEvidenceView[];

	constructor(payload: DocumentFieldEntryPayload) {
		this.type = 'field';
		this.key = payload.key;
		this.uuid = payload.uuid;
		this.path = payload.path;
		this.label = payload.label;
		this.description = payload.description;
		this.fieldType = new LabeledValueView(payload.fieldType);
		this.isMultiple = payload.isMultiple;
		this.value = resolveValue(payload.value);
		this.options =
			payload.options === null ? null : payload.options.map((option) => new LabeledValueView(option));
		this.verification = new LabeledValueView(payload.verification);
		this.isPendingReview = payload.isPendingReview;
		this.isRejectedValue = payload.isRejectedValue;
		this.canConfirm = payload.canConfirm;
		this.reviewedById = payload.reviewedBy === null ? null : payload.reviewedBy.id;
		this.reviewedAt = payload.reviewedAt;
		this.evidence = payload.evidence.map(buildDocumentEvidenceView);

		Object.freeze(this);
	}

	/** `null` only: the server turns a blank or empty text value into `null`, never an empty string.
	 * Displays "Aucune valeur" — never "Non abordé": this screen does not qualify an absent value. */
	get isEmpty(): boolean {
		return this.value === null;
	}
}

export default DocumentFieldView;
